import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import api from '../../api/api'
import { LifeBuoy, ChevronLeft, Plus, X, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'

const STATUS_META = {
    open:        { label: 'Open',        cls: 'bg-blue-100 text-blue-700' },
    in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700' },
    resolved:    { label: 'Resolved',    cls: 'bg-green-100 text-green-700' },
    closed:      { label: 'Closed',      cls: 'bg-gray-100 text-gray-500' },
}

const ROLE_LABEL = { admin: 'Support Team', seller: 'Merchant', customer: 'You' }

const StatusBadge = ({ status }) => {
    const m = STATUS_META[status] || STATUS_META.open
    return <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${m.cls}`}>{m.label}</span>
}

const SupportTickets = () => {
    const { userInfo } = useSelector(s => s.auth)

    const [tickets, setTickets] = useState([])
    const [activeTicket, setActiveTicket] = useState(null)
    const [loading, setLoading] = useState(true)

    const [showNew, setShowNew] = useState(false)
    const [subject, setSubject] = useState('')
    const [message, setMessage] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const [replyText, setReplyText] = useState('')
    const [sending, setSending] = useState(false)

    const fetchTickets = async () => {
        try {
            const { data } = await api.get('/home/support-tickets', { withCredentials: true })
            setTickets(data.tickets || [])
        } catch {
            // silent
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchTickets()
    }, [])

    const handleCreate = async () => {
        if (!subject.trim() || !message.trim()) return
        setSubmitting(true)
        try {
            const { data } = await api.post('/home/support-ticket', {
                subject: subject.trim(),
                message: message.trim(),
            }, { withCredentials: true })
            toast.success(data.message || 'Ticket submitted')
            setTickets(prev => [data.ticket, ...prev])
            setSubject('')
            setMessage('')
            setShowNew(false)
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to submit ticket')
        } finally {
            setSubmitting(false)
        }
    }

    const openTicket = async (ticketId) => {
        try {
            const { data } = await api.get(`/home/support-ticket/${ticketId}`, { withCredentials: true })
            setActiveTicket(data.ticket)
        } catch {
            toast.error('Failed to load ticket')
        }
    }

    const handleSend = async () => {
        if (!replyText.trim()) return
        setSending(true)
        try {
            const { data } = await api.post(`/home/support-ticket/${activeTicket._id}/response`, {
                message: replyText.trim()
            }, { withCredentials: true })
            setActiveTicket(data.ticket)
            setTickets(prev => prev.map(t => t._id === data.ticket._id ? { ...t, status: data.ticket.status } : t))
            setReplyText('')
            toast.success(data.message || 'Reply sent')
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to send reply')
        } finally {
            setSending(false)
        }
    }

    if (activeTicket) {
        return (
            <div className="p-5">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setActiveTicket(null)}
                                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
                            >
                                <ChevronLeft size={16} /> Back
                            </button>
                            <div>
                                <p className="font-semibold text-gray-900">{activeTicket.subject}</p>
                                <p className="text-xs text-gray-400">#{activeTicket._id.slice(-8).toUpperCase()}</p>
                            </div>
                        </div>
                        <StatusBadge status={activeTicket.status} />
                    </div>

                    <div className="px-5 py-5 space-y-4 max-h-[55vh] overflow-y-auto">
                        {/* Original */}
                        <div className="flex gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 shrink-0">
                                {userInfo?.name?.[0]?.toUpperCase() || 'Y'}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-sm font-medium text-gray-800">You</span>
                                    <span className="text-xs text-gray-400">{new Date(activeTicket.createdAt).toLocaleString()}</span>
                                </div>
                                <p className="text-sm text-gray-700 mt-1 bg-gray-50 rounded-xl p-3 leading-relaxed">{activeTicket.message}</p>
                            </div>
                        </div>

                        {/* Responses */}
                        {activeTicket.responses?.map((r, i) => {
                            const isSupport = r.responderRole === 'admin'
                            return (
                                <div key={i} className={`flex gap-3 ${isSupport ? 'flex-row-reverse' : ''}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                                        isSupport ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'
                                    }`}>
                                        {isSupport ? 'S' : userInfo?.name?.[0]?.toUpperCase() || 'Y'}
                                    </div>
                                    <div className="flex-1">
                                        <div className={`flex items-baseline gap-2 ${isSupport ? 'justify-end' : ''}`}>
                                            <span className="text-sm font-medium text-gray-800">{ROLE_LABEL[r.responderRole]}</span>
                                            <span className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleString()}</span>
                                        </div>
                                        <p className={`text-sm text-gray-700 mt-1 rounded-xl p-3 leading-relaxed ${
                                            isSupport ? 'bg-indigo-50' : 'bg-gray-50'
                                        }`}>
                                            {r.message}
                                        </p>
                                    </div>
                                </div>
                            )
                        })}

                        {activeTicket.status === 'resolved' && (
                            <div className="flex items-center gap-2 text-green-600 text-sm justify-center py-2">
                                <CheckCircle2 size={16} />
                                This ticket has been resolved. Reply if you need further assistance.
                            </div>
                        )}
                    </div>

                    {activeTicket.status !== 'closed' && (
                        <div className="px-5 py-4 border-t border-gray-100 space-y-2">
                            <textarea
                                value={replyText}
                                onChange={e => setReplyText(e.target.value)}
                                placeholder="Add a reply…"
                                rows={3}
                                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
                            />
                            <div className="flex justify-end">
                                <button
                                    onClick={handleSend}
                                    disabled={sending || !replyText.trim()}
                                    className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                                >
                                    {sending ? 'Sending…' : 'Send Reply'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="p-5">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <LifeBuoy size={20} className="text-indigo-500" />
                        <h2 className="text-lg font-semibold text-gray-900">Help & Support</h2>
                    </div>
                    <button
                        onClick={() => setShowNew(v => !v)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 font-medium"
                    >
                        {showNew ? <X size={15} /> : <Plus size={15} />}
                        {showNew ? 'Cancel' : 'New Ticket'}
                    </button>
                </div>

                {showNew && (
                    <div className="px-5 py-5 border-b border-gray-100 bg-gray-50 space-y-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
                            <input
                                type="text"
                                value={subject}
                                onChange={e => setSubject(e.target.value)}
                                placeholder="Brief description of your issue"
                                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
                            <textarea
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                placeholder="Describe your issue in detail…"
                                rows={4}
                                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={handleCreate}
                                disabled={submitting || !subject.trim() || !message.trim()}
                                className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                            >
                                {submitting ? 'Submitting…' : 'Submit Ticket'}
                            </button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="py-12 flex justify-center text-gray-400 text-sm">Loading…</div>
                ) : tickets.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                        <LifeBuoy size={40} className="mb-3 text-gray-300" />
                        <p className="font-medium text-gray-500">No support tickets</p>
                        <p className="text-sm mt-1">Open a ticket if you need help.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {tickets.map(t => (
                            <button
                                key={t._id}
                                onClick={() => openTicket(t._id)}
                                className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors flex items-start justify-between gap-4"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-gray-800 truncate">{t.subject}</span>
                                        <StatusBadge status={t.status} />
                                    </div>
                                    <p className="text-xs text-gray-400 mt-0.5">#{t._id.slice(-8).toUpperCase()}</p>
                                </div>
                                <div className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                                    {new Date(t.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default SupportTickets
